import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  ProblemCreate,
  ProblemDetail,
  ProblemFilter,
  ProblemRecord,
  ProblemStatus,
  ProblemUpdate,
} from './problems.types';

// Cloud manager scope (alongside /itsm/commons); mirrors down to the sites.
const BASE = '/users/v1/itsm/problems';
const KEY = ['problems'] as const;

export function useProblems(filter: ProblemFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.class_code) params.set('class_code', filter.class_code);
  const qs = params.toString();
  return useQuery({
    queryKey: [...KEY, filter],
    queryFn: () => api.get<ProblemRecord[]>(`${BASE}${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000,
  });
}

/** Problem detail — includes the S-6 linked-incidents list (embedded on GET). */
export function useProblem(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<ProblemDetail>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useCreateProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProblemCreate) => api.post<ProblemRecord>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ProblemUpdate & { id: string }) =>
      api.post<ProblemRecord>(`${BASE}/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Move a problem along the machine. Entering `known_error` requires a
 * workaround (stored or supplied here) — the backend surfaces a 400 otherwise. */
export function useTransitionProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, workaround }: { id: string; status: ProblemStatus; workaround?: string }) =>
      api.post<ProblemRecord>(`${BASE}/${id}/transition`, { status, workaround }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
